// Shared DOM queries and focus helpers for the active guess row.

import { scrollBehavior } from './preferences.js'

// Calls `init` on every element matching `selector`: those on the page now
// and any htmx adds later (e.g. the finish modal and its chart).
export const onElements = (selector, init) => {
    const initWithin = (root) => {
        if (!(root instanceof Element)) return
        const matches = root.matches(selector) ? [root] : []
        matches
            .concat(Array.from(root.querySelectorAll(selector)))
            .forEach(init)
    }

    initWithin(document.body)
    document.body.addEventListener('htmx:after:settle', (event) => {
        event.detail.newContent.forEach(initWithin)
    })
}

// Excludes static rows (e.g. the header logo), which reuse the guess-row
// markup for its styling but aren't guesses and never animate.
export const getValidWordRows = () =>
    document.querySelectorAll('.valid-word-row:not(.valid-word-row--static)')

export const getLastValidWordTiles = () => {
    const rows = getValidWordRows()
    if (!rows.length) return []
    return rows[rows.length - 1].querySelectorAll('.valid-word-letter')
}

// No-op for rows without a star.
const revealRowStar = (row) => {
    const star = row?.querySelector('.tier-star')
    if (!star) return
    star.classList.remove('opacity-0')
    star.classList.add('animate-star-pop')
}

// Staggers `animate-flip` across a row's tiles, then reveals its tier
// star (if any) once the last tile's flip finishes.
export const revealTiles = (row, tiles) => {
    tiles.forEach((tile, index) => {
        setTimeout(() => tile.classList.add('animate-flip'), index * 150)
    })

    const lastTile = tiles[tiles.length - 1]
    lastTile?.addEventListener('animationend', () => revealRowStar(row), {
        once: true,
    })
}

export const getActiveRow = () => document.querySelector('#active-row')

// The active row plus its Submit/Clear buttons, all of which need to stay
// above the on-screen keyboard while typing.
export const getGuessForm = () => document.getElementById('guess-form')

// The single input all typing goes into (see game_session/today.html).
export const getGuessInput = () => document.getElementById('guess-input')

export const getLetterTiles = () => {
    const row = getActiveRow()
    return row ? row.querySelectorAll('.letter-tile') : []
}

// The active row's tiles the player types into, i.e. not the given first
// and last letters.
export const getEditableLetterTiles = () => {
    const row = getActiveRow()
    return row ? Array.from(row.querySelectorAll('[data-editable-tile]')) : []
}

const SCROLL_GAP = 16

export const getScrollParent = (element) => {
    for (let node = element.parentElement; node; node = node.parentElement) {
        if (/auto|scroll/.test(getComputedStyle(node).overflowY)) return node
    }
    return null
}

// Scrolls whichever container holds `element` (on the game page, <main>)
// only as far as needed to bring it into view, so it sits just above the
// on-screen keyboard rather than jumping to the top.
//
// The visible area is measured from the visual viewport, which shrinks (and
// on iOS, pans) for the keyboard while the layout viewport doesn't. Only the
// container is scrolled: native scrollIntoView also scrolls the window,
// which on iOS shoves the whole page up under the header.
export const scrollIntoView = (element) => {
    if (!element) return
    const behavior = scrollBehavior()
    const container = getScrollParent(element)
    if (!container) {
        element.scrollIntoView({ block: 'nearest', behavior })
        return
    }

    const viewport = window.visualViewport
    const viewTop = viewport?.offsetTop ?? 0
    const viewBottom = viewTop + (viewport?.height ?? window.innerHeight)
    const box = container.getBoundingClientRect()
    const top = Math.max(box.top, viewTop) + SCROLL_GAP
    const bottom = Math.min(box.bottom, viewBottom) - SCROLL_GAP
    const rect = element.getBoundingClientRect()

    // Bring the bottom up to the keyboard, but never so far that the top
    // goes out of view.
    let delta = 0
    if (rect.top < top) delta = rect.top - top
    else if (rect.bottom > bottom)
        delta = Math.min(rect.bottom - bottom, rect.top - top)
    if (Math.abs(delta) < 1) return

    container.scrollBy({ top: delta, behavior })
}

// The browser's own focus scrolling (which, on iOS, also clears the
// keyboard) only brings the hidden input into view. scrollIntoView then
// makes sure the whole form - row and Submit/Clear buttons - is visible.
export const focusGuessInput = () => {
    const input = getGuessInput()
    if (!input || input.disabled) return
    input.focus()
    scrollIntoView(getGuessForm())
}

// Adds `classes` to each element for `ms`, e.g. to run a one-off animation.
export const flashClasses = (elements, classes, ms) => {
    elements.forEach((element) => {
        element.classList.add(...classes)
        setTimeout(() => element.classList.remove(...classes), ms)
    })
}

// Briefly shakes the given tiles with a red border to flag a rejected entry.
// Pass `!important` border classes (as the default does) so they beat the
// tiles' base border regardless of CSS order.
export const shakeTiles = (tiles, borderClass = 'border-red-500!') =>
    flashClasses(tiles, ['animate-shake', borderClass], 400)
